import React, { useState, useCallback, useEffect } from "react";
import { Canvas } from "react-three-fiber";
import classNames from "classnames";
import { Helmet } from "react-helmet";

import { State, Language } from "../core";

import "./App.scss";
import { Locus } from "./locus/Locus";
import { audioCtx, Recorder, Player, getMicrophoneStream } from "../audio";
import { sendSound } from "../soundStore";
import { Introduction } from "./Introduction";
import { getString, getDefaultLanguage, setDefaultLanguage } from "./strings";
import { InfoModal } from "./InfoModal";
import { Message } from "./Message";
import { Scene } from "./Scene";
import { Choir } from "./Choir";
import {
  INDICATOR_SHRINK_DURATION,
  SHRINK_TRAVEL_DELAY,
  CENTER_TRAVEL_DURATION
} from "../constants";
import { ActionLabel } from "./ActionLabel";
import { ThankYou } from "./ThankYou";
import * as consent from "./consent";
import { ConsentModal } from "./ConsentModal";

function App() {
  let [language, setLanguage] = useState<Language>(getDefaultLanguage());
  let [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);
  let [consentModalOpen, setConsentModalOpen] = useState<boolean>(false);
  let [state, setState] = useState<State>({ state: "start" });
  let [isRecorderRecording, setRecorderRecording] = useState(false);
  let [responsePlayCount, setResponsePlayCount] = useState(0);
  let [responseNumber, setResponseNumber] = useState(0);

  let start = useCallback(() => {
    if (consent.isConsentGiven()) {
      audioCtx.resume();
      setState({ state: "requestingMicrophone" });
    } else {
      setTimeout(() => setConsentModalOpen(true), 200);
    }
  }, []);

  let onConsentGiven = useCallback(() => {
    setConsentModalOpen(false);
    consent.setConsentGiven(true);
    start();
  }, []);

  let onConsentNotGiven = useCallback(() => setConsentModalOpen(false), []);

  useEffect(
    function openMicrophoneStream() {
      if (state.state === "requestingMicrophone") {
        getMicrophoneStream()
          .then(micStream => {
            let newRecorder = new Recorder(micStream, recordedSound => {
              setState({ state: "pending", player: new Player(recordedSound) });
            });
            setState({ state: "recording", recorder: newRecorder });
          })
          .catch(err => {
            console.error("Microphone problem", err);
            setState({ state: "microphoneRequestFailed" });
          });
      }
    },
    [state]
  );

  useEffect(
    function startRecording() {
      if (state.state === "recording") {
        state.recorder.addEventListener(
          "start",
          () => setRecorderRecording(true),
          { once: true }
        );
        state.recorder.start();
      }
    },
    [state]
  );

  useEffect(
    function startPlaying() {
      if (state.state === "pending") {
        state.player.start();
      }
    },
    [state]
  );

  let onActionClick = useCallback(() => {
    if (
      state.state === "start" ||
      state.state === "restart" ||
      state.state === "microphoneRequestFailed" ||
      state.state === "sendingFailed"
    ) {
      start();
    } else if (state.state === "recording") {
      state.recorder.stop();
    } else if (state.state === "pending") {
      let pendingState = state;
      state.player.stop();
      setState({ state: "sending", player: pendingState.player });
      let responsePlayCallback = () => setResponsePlayCount(pc => pc + 1);
      sendSound(
        pendingState.player.sound,
        INDICATOR_SHRINK_DURATION +
          SHRINK_TRAVEL_DELAY +
          CENTER_TRAVEL_DURATION,
        responsePlayCallback
      )
        .then(({ onDone, responseNumber }) => {
          setResponseNumber(responseNumber);
          setState({ state: "sent", player: pendingState.player });
          setRecorderRecording(false);
          onDone.then(() => {
            setState({ state: "restart" });
          });
        })
        .catch(err => {
          console.error("Could not send sound", err);
          setState({ state: "sendingFailed" });
        });
    }
  }, [state, start]);

  let onDiscard = useCallback(() => {
    if (state.state === "pending") {
      state.player.stop();
    }
    start();
  }, [state, start]);

  let onChangeLanguage = useCallback((lang: Language) => {
    setDefaultLanguage(lang);
    setLanguage(lang);
  }, []);

  let onOpenInfoModal = useCallback(() => setInfoModalOpen(true), []);
  let onCloseInfoModal = useCallback(() => setInfoModalOpen(false), []);

  return (
    <div
      className={classNames("app", state.state)}
      onClick={() => audioCtx.resume()}
    >
      <Helmet>
        <title>{getString(language, "title")}</title>
      </Helmet>
      <Canvas style={{ touchAction: "none" }}>
        <fog attach="fog" color={"white"} near={6} far={20} />
        <Scene state={state}>
          <ambientLight intensity={0.75} />
          <pointLight position={[0, 75, 150]} intensity={0.75} />
          <Locus
            state={state}
            isRecorderRecording={isRecorderRecording}
            onActionClick={onActionClick}
          />
          <Choir state={state} responsePlayCount={responsePlayCount} />
        </Scene>
      </Canvas>
      <ActionLabel
        stringKey="recordButton"
        forStates={["start", "microphoneRequestFailed", "sendingFailed"]}
        language={language}
      />
      <ActionLabel
        stringKey={isRecorderRecording ? "doneButton" : "startingButton"}
        forStates={["recording"]}
        language={language}
      />
      <ActionLabel
        stringKey="sendButton"
        forStates={["pending"]}
        language={language}
      />
      <button
        className={classNames("secondaryAction", {
          isVisible: state.state === "pending"
        })}
        onClick={onDiscard}
      >
        {getString(language, "discardButton")}
      </button>
      <Introduction
        state={state}
        language={language}
        onOpenInfoModal={onOpenInfoModal}
        onChangeLanguage={onChangeLanguage}
      />
      <Message state={state} language={language} />
      <ThankYou
        state={state}
        language={language}
        responseNumber={responseNumber}
      />
      <InfoModal
        isOpen={infoModalOpen}
        language={language}
        onClose={onCloseInfoModal}
      />
      <ConsentModal
        isOpen={consentModalOpen}
        language={language}
        onConsent={onConsentGiven}
        onDoNotConsent={onConsentNotGiven}
      />
    </div>
  );
}

export default App;
